import { FileSpec, useConfig } from '@chainlit/react-client';
import type { FileWithPath } from 'react-dropzone';

import { Translator } from '@/components/i18n';
import { PaperClip } from '@/components/icons/PaperClip';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { useUpload } from '@/hooks/useUpload';
import { useFileUploadSettings } from '@/hooks/useFileUploadSettings';
import { type FileUploadSettings } from '@/state/uiSettings';
import { DEFAULT_ALLOWED_FILE_EXTENSIONS, DEFAULT_MAX_FILE_SIZE_MB, DEFAULT_MAX_FILES_COUNT } from '@/utils/fileValidation';

interface UploadButtonProps {
  disabled?: boolean;
  webSearchEnabled?: boolean;
  fileSpec: FileSpec;
  onFileUpload: (files: FileWithPath[]) => void;
  onFileUploadError: (error: string) => void;
}

export const UploadButton = ({
  disabled = false,
  webSearchEnabled = false,
  fileSpec,
  onFileUpload,
  onFileUploadError
}: UploadButtonProps) => {
  const { config } = useConfig();
  const uploadSettings = useFileUploadSettings();

  // Align accepted file types with supported RAG document formats and backend settings
  const restrictedFileSpec: FileSpec = {
    ...fileSpec,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx'
      ],
      'text/plain': ['.txt'],
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
      ],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
        '.pptx'
      ],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    max_files: uploadSettings.maxFiles,
    max_size_mb: uploadSettings.maxSizeMB
  };

  const upload = useUpload({
    spec: restrictedFileSpec,
    onResolved: (payloads: FileWithPath[]) => {
      onFileUpload(payloads);
    },
    onError: (error: string) => {
      console.error('[UploadButton] Upload error', { error });
      onFileUploadError(error);
    },
    options: { noClick: false }
  });

  if (!upload) {
    return null;
  }

  const { getRootProps, getInputProps } = upload;
  
  if (!config?.features.spontaneous_file_upload?.enabled) {
    return null;
  }

  return (
    <div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">
              <input
                id="upload-button-input"
                className="hidden"
                {...getInputProps()}
              />
              <Button
                id={disabled ? 'upload-button-loading' : 'upload-button'}
                variant="ghost"
                size="icon"
                className="hover:bg-muted"
                disabled={disabled}
                {...getRootProps()}
              >
                <PaperClip className="!size-6" />
              </Button>
            </span>
          </TooltipTrigger>          <TooltipContent>
            {disabled && webSearchEnabled ? (
              <p>Document Upload is disabled when Web Search is ON</p>
            ) : (
              <p>
                <Translator path="chat.input.actions.attachFiles" />
                <br />
                <span className="text-xs text-muted-foreground">
                  Allowed: {uploadSettings.allowedExtensions.map((ext: string) => ext.toUpperCase()).join(', ')}
                  <br />
                  Max: {uploadSettings.maxFiles} files · {uploadSettings.maxSizeMB}MB each
                </span>
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default UploadButton;
